/**
 * Checkout journey session scope — URL params + TanStack Query (IMP-06, GC-P1-04).
 */
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useGetCheckoutSession,
  usePaymentStatus,
  useResolvePostKioskToken,
  type CheckoutSessionDTO,
} from '../checkoutApi.js';
import { sessionLog } from '../logging.js';

export interface CheckoutSessionScopeState {
  readonly checkoutSessionPublicId: string | null;
  readonly paymentId: string | null;
  /** Known order/transaction id from session or payment status; null when unknown. */
  readonly transactionId: number | null;
  readonly session: CheckoutSessionDTO | undefined;
  readonly isSessionLoading: boolean;
}

function trimParam(value: string | null | undefined): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeTransactionId(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null;
  }
  return value;
}

export function useCheckoutSession(): CheckoutSessionScopeState {
  const [params] = useSearchParams();
  const sessionIdParam = trimParam(params.get('sessionId'));
  const sessionIdStripeParam = trimParam(params.get('session_id'));
  const paymentIdParam = trimParam(params.get('paymentId'));
  const postKioskToken = trimParam(params.get('token'));

  const postKioskResolution = useResolvePostKioskToken(postKioskToken);
  const paymentIdFromPostKiosk =
    postKioskToken !== null
      ? trimParam(postKioskResolution.data?.paymentId)
      : null;

  const resolvedPaymentId = paymentIdParam ?? paymentIdFromPostKiosk;

  const sessionIdFromUrl = sessionIdParam ?? sessionIdStripeParam;

  const paymentStatusQuery = usePaymentStatus(resolvedPaymentId, undefined, {
    enabled: resolvedPaymentId !== null,
  });
  const checkoutSessionPublicIdFromStatus = trimParam(
    paymentStatusQuery.data?.checkoutSessionPublicId,
  );

  const checkoutSessionPublicId =
    sessionIdFromUrl ?? checkoutSessionPublicIdFromStatus;

  const sessionQuery = useGetCheckoutSession(checkoutSessionPublicId, 'active');

  useEffect(() => {
    if (!sessionQuery.isError) {
      return;
    }
    sessionLog.error('Checkout session load failed', sessionQuery.error, {
      operation: 'getCheckoutSession',
      hasSessionId: checkoutSessionPublicId !== null,
    });
  }, [checkoutSessionPublicId, sessionQuery.error, sessionQuery.isError]);

  useEffect(() => {
    if (!paymentStatusQuery.isError) {
      return;
    }
    sessionLog.error('Payment status load failed', paymentStatusQuery.error, {
      operation: 'paymentStatus',
      hasPaymentId: resolvedPaymentId !== null,
    });
  }, [paymentStatusQuery.error, paymentStatusQuery.isError, resolvedPaymentId]);

  useEffect(() => {
    if (postKioskToken === null || !postKioskResolution.isError) {
      return;
    }
    // Never log post-kiosk token value.
    sessionLog.error('Post-kiosk token resolve failed', postKioskResolution.error, {
      operation: 'resolvePostKioskToken',
    });
  }, [postKioskResolution.error, postKioskResolution.isError, postKioskToken]);

  const paymentId = useMemo((): string | null => {
    return (
      resolvedPaymentId ??
      trimParam(sessionQuery.data?.paymentId) ??
      trimParam(paymentStatusQuery.data?.paymentId)
    );
  }, [paymentStatusQuery.data?.paymentId, resolvedPaymentId, sessionQuery.data?.paymentId]);

  const transactionId = useMemo((): number | null => {
    return (
      normalizeTransactionId(sessionQuery.data?.transactionId) ??
      normalizeTransactionId(paymentStatusQuery.data?.transactionId)
    );
  }, [paymentStatusQuery.data?.transactionId, sessionQuery.data?.transactionId]);

  return useMemo((): CheckoutSessionScopeState => {
    const sessionPublicId =
      checkoutSessionPublicId ?? trimParam(sessionQuery.data?.id);

    return {
      checkoutSessionPublicId: sessionPublicId,
      paymentId,
      transactionId,
      session: sessionQuery.data,
      isSessionLoading:
        sessionPublicId !== null &&
        sessionQuery.data === undefined &&
        (sessionQuery.isLoading || sessionQuery.isFetching),
    };
  }, [
    checkoutSessionPublicId,
    paymentId,
    transactionId,
    sessionQuery.data,
    sessionQuery.isFetching,
    sessionQuery.isLoading,
  ]);
}
